/**
 * O jardim de cada pessoa: o `garden_stats.dart` e o `garden_service.dart`
 * juntos, sem a parte que grava.
 *
 * Vive um documento por pessoa em `garden_stats`, com o slug dela no lugar do
 * identificador — o comentário do serviço de lá explica: o armazém não tem
 * subcoleção, e o jardim de cada uma é um documento só.
 */

import { gravarData, lerData } from '../nucleo/tempo';

export interface Jardim {
  currentStage: number;
  lastVisit: Date;
  streakCount: number;
  totalInteractions: number;
  plantType: string;
}

/** `GardenStats.initial()`: o vaso vazio de quem nunca passou por aqui. */
export function jardimVazio(agora: Date = new Date()): Jardim {
  return {
    currentStage: 0,
    lastVisit: agora,
    streakCount: 0,
    totalInteractions: 0,
    plantType: 'lily',
  };
}

/** `GardenStats.fromMap`. */
export function jardimDeMapa(dados: Record<string, unknown> | null | undefined): Jardim {
  if (!dados) return jardimVazio();
  return {
    currentStage: numero(dados.currentStage, 0),
    lastVisit: lerData(dados.lastVisit) ?? new Date(),
    streakCount: numero(dados.streakCount, 0),
    totalInteractions: numero(dados.totalInteractions, 0),
    plantType: typeof dados.plantType === 'string' ? dados.plantType : 'lily',
  };
}

/** `GardenStats.toMap`. */
export function jardimParaMapa(j: Jardim): Record<string, unknown> {
  return {
    currentStage: j.currentStage,
    lastVisit: gravarData(j.lastVisit),
    streakCount: j.streakCount,
    totalInteractions: j.totalInteractions,
    plantType: j.plantType,
  };
}

/**
 * `_calculateStage`: a fase que tantas visitas compram. Os cortes são os de
 * lá, e não uma curva — 30 visitas é a flor aberta e ponto.
 */
export function faseDeVisitas(visitas: number): number {
  if (visitas >= 30) return 5;
  if (visitas >= 20) return 4;
  if (visitas >= 10) return 3;
  if (visitas >= 5) return 2;
  if (visitas >= 1) return 1;
  return 0;
}

/**
 * `recordInteraction`, só a conta: recebe o que está gravado e devolve o que
 * tem de ser gravado. Quem grava é a tela, com o armazém dela.
 *
 * Sem documento nenhum a planta já nasce brotando (fase 1), como lá.
 */
export function comAVisitaDeHoje(
  atual: Jardim | null,
  agora: Date = new Date(),
): Jardim {
  if (!atual) {
    return {
      currentStage: 1,
      lastVisit: agora,
      streakCount: 1,
      totalInteractions: 1,
      plantType: 'lily',
    };
  }

  let sequencia = atual.streakCount;
  if (foiOntem(atual.lastVisit, agora)) {
    sequencia += 1;
  } else if (!foiHoje(atual.lastVisit, agora)) {
    sequencia = 1; // A sequência quebrou.
  }
  // Se já foi hoje, a sequência fica como está.

  const visitas = atual.totalInteractions + 1;

  return {
    ...atual,
    currentStage: faseDeVisitas(visitas),
    lastVisit: agora,
    streakCount: sequencia,
    totalInteractions: visitas,
  };
}

/** `_isToday`: mesmo dia do calendário, e não vinte e quatro horas. */
export function foiHoje(quando: Date, agora: Date = new Date()): boolean {
  return (
    quando.getFullYear() === agora.getFullYear() &&
    quando.getMonth() === agora.getMonth() &&
    quando.getDate() === agora.getDate()
  );
}

/** `_isYesterday`. */
export function foiOntem(quando: Date, agora: Date = new Date()): boolean {
  const ontem = new Date(agora.getTime());
  ontem.setDate(ontem.getDate() - 1);
  return foiHoje(quando, ontem);
}

const numero = (valor: unknown, padrao: number): number =>
  typeof valor === 'number' && Number.isFinite(valor) ? valor : padrao;
